// =====================================================================================
// VideoErrorHandler - Centralized error classification and recovery strategies
// =====================================================================================
// Phase 4: Error Handling - Provides user-friendly error messages and recovery actions

import type { VideoError } from './types.js';

export interface ErrorClassification {
  category: ErrorCategory;
  severity: ErrorSeverity;
  userMessage: string;
  technicalMessage: string;
  recoveryActions: RecoveryAction[];
  shouldRetry: boolean;
  isTemporary: boolean;
}

export interface RecoveryAction {
  type: RecoveryActionType;
  label: string;
  description: string;
  priority: number; // 1-5, 1 being highest priority
}

export type ErrorCategory =
  | 'network'
  | 'permission'
  | 'server'
  | 'authentication'
  | 'configuration'
  | 'device'
  | 'unknown';

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

export type RecoveryActionType =
  | 'retry'
  | 'check_permission'
  | 'check_network'
  | 'refresh_page'
  | 'contact_support'
  | 'check_device'
  | 'wait_and_retry';

/**
 * Centralized error handler for video-related errors
 * Provides classification, user-friendly messages, and recovery strategies
 */
export class VideoErrorHandler {
  private readonly errorClassifications = new Map<string, ErrorClassification>();
  private readonly fallbackClassification: ErrorClassification;

  constructor() {
    this.initializeErrorClassifications();
    this.fallbackClassification = this.createFallbackClassification();
  }

  /**
   * Classify an error and provide user-friendly information
   */
  classifyError(error: VideoError | string): ErrorClassification {
    // Handle null, undefined, or invalid inputs
    if (!error) {
      return this.fallbackClassification;
    }

    const errorCode = typeof error === 'string' ? error : error?.code || 'UNKNOWN';
    const classification = this.errorClassifications.get(errorCode);

    if (classification) {
      return classification;
    }

    // Try to classify by error pattern
    return this.classifyByPattern(errorCode) || this.fallbackClassification;
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(error: VideoError | string): string {
    return this.classifyError(error).userMessage;
  }

  /**
   * Get recovery actions for an error
   */
  getRecoveryActions(error: VideoError | string): RecoveryAction[] {
    return this.classifyError(error).recoveryActions;
  }

  /**
   * Check if error should trigger automatic retry
   */
  shouldAutoRetry(error: VideoError | string): boolean {
    const classification = this.classifyError(error);
    return classification.shouldRetry && classification.isTemporary;
  }

  /**
   * Get the most appropriate recovery action
   */
  getPrimaryRecoveryAction(error: VideoError | string): RecoveryAction | null {
    const actions = this.getRecoveryActions(error);
    return actions.length > 0 ? actions[0] : null;
  }

  /**
   * Create a comprehensive error summary
   */
  createErrorSummary(error: VideoError): {
    classification: ErrorClassification;
    timestamp: string;
    errorId: string;
  } {
    const classification = this.classifyError(error);
    const timestamp = new Date(error.timestamp || Date.now()).toISOString();
    const errorId = this.generateErrorId(error);

    return {
      classification,
      timestamp,
      errorId
    };
  }

  private initializeErrorClassifications(): void {
    // Network-related errors
    this.addErrorClassification('CONNECTION_FAILED', {
      category: 'network',
      severity: 'medium',
      userMessage: 'Unable to connect to video service. Please check your internet connection.',
      technicalMessage: 'Failed to establish WebRTC connection',
      recoveryActions: [
        this.createRecoveryAction('retry', 'Try Again', 'Attempt to reconnect to the video service', 1),
        this.createRecoveryAction('check_network', 'Check Network', 'Verify your internet connection is stable', 2),
      ],
      shouldRetry: true,
      isTemporary: true
    });

    this.addErrorClassification('NETWORK_TIMEOUT', {
      category: 'network',
      severity: 'medium',
      userMessage: 'Connection timed out. Please try again.',
      technicalMessage: 'Network request timed out',
      recoveryActions: [
        this.createRecoveryAction('retry', 'Try Again', 'Retry the connection', 1),
        this.createRecoveryAction('check_network', 'Check Network', 'Verify network stability', 2),
      ],
      shouldRetry: true,
      isTemporary: true
    });

    this.addErrorClassification('UNEXPECTED_DISCONNECT', {
      category: 'network',
      severity: 'medium',
      userMessage: 'Video connection was lost. Attempting to reconnect...',
      technicalMessage: 'WebRTC connection unexpectedly terminated',
      recoveryActions: [
        this.createRecoveryAction('retry', 'Reconnect', 'Automatically reconnecting to video', 1),
      ],
      shouldRetry: true,
      isTemporary: true
    });

    // Permission-related errors
    this.addErrorClassification('PERMISSION_DENIED', {
      category: 'permission',
      severity: 'high',
      userMessage: 'Camera or microphone permission denied. Please allow access to join video calls.',
      technicalMessage: 'Media device permission not granted',
      recoveryActions: [
        this.createRecoveryAction('check_permission', 'Grant Permission', 'Allow camera and microphone access in your browser', 1),
        this.createRecoveryAction('refresh_page', 'Refresh Page', 'Refresh and try granting permission again', 2),
      ],
      shouldRetry: false,
      isTemporary: false
    });

    this.addErrorClassification('DEVICE_NOT_FOUND', {
      category: 'device',
      severity: 'high',
      userMessage: 'No camera or microphone found. Please connect a device and try again.',
      technicalMessage: 'Required media devices not available',
      recoveryActions: [
        this.createRecoveryAction('check_device', 'Check Device', 'Ensure camera/microphone are connected and working', 1),
        this.createRecoveryAction('refresh_page', 'Refresh Page', 'Refresh page after connecting devices', 2),
      ],
      shouldRetry: false,
      isTemporary: false
    });

    // Server-related errors
    this.addErrorClassification('SERVER_ERROR', {
      category: 'server',
      severity: 'high',
      userMessage: 'Video service is temporarily unavailable. Please try again later.',
      technicalMessage: 'Video server returned an error',
      recoveryActions: [
        this.createRecoveryAction('wait_and_retry', 'Wait and Retry', 'Wait a moment and try again', 1),
        this.createRecoveryAction('contact_support', 'Contact Support', 'Report this issue if it persists', 3),
      ],
      shouldRetry: true,
      isTemporary: true
    });

    this.addErrorClassification('TOKEN_TIMEOUT', {
      category: 'server',
      severity: 'medium',
      userMessage: 'Video service is taking too long to respond. Please try again.',
      technicalMessage: 'Video token request timed out',
      recoveryActions: [
        this.createRecoveryAction('retry', 'Try Again', 'Retry connecting to video', 1),
        this.createRecoveryAction('wait_and_retry', 'Wait and Retry', 'Wait a moment before retrying', 2),
      ],
      shouldRetry: true,
      isTemporary: true
    });

    // Authentication errors
    this.addErrorClassification('INVALID_TOKEN', {
      category: 'authentication',
      severity: 'high',
      userMessage: 'Video session expired. Please refresh and try again.',
      technicalMessage: 'Video access token is invalid or expired',
      recoveryActions: [
        this.createRecoveryAction('refresh_page', 'Refresh Page', 'Refresh to get a new session', 1),
        this.createRecoveryAction('retry', 'Try Again', 'Attempt to reconnect', 2),
      ],
      shouldRetry: true,
      isTemporary: true
    });

    this.addErrorClassification('UNAUTHORIZED', {
      category: 'authentication',
      severity: 'high',
      userMessage: 'You are not authorized to join this video call.',
      technicalMessage: 'Insufficient permissions for video room access',
      recoveryActions: [
        this.createRecoveryAction('contact_support', 'Contact Support', 'Contact support if you believe this is an error', 1),
      ],
      shouldRetry: false,
      isTemporary: false
    });

    // Device control errors
    this.addErrorClassification('MIC_TOGGLE_FAILED', {
      category: 'device',
      severity: 'low',
      userMessage: 'Unable to toggle microphone. Please try again.',
      technicalMessage: 'Failed to enable/disable microphone',
      recoveryActions: [
        this.createRecoveryAction('retry', 'Try Again', 'Try toggling microphone again', 1),
        this.createRecoveryAction('check_permission', 'Check Permission', 'Verify microphone permissions', 2),
      ],
      shouldRetry: true,
      isTemporary: true
    });

    this.addErrorClassification('CAMERA_TOGGLE_FAILED', {
      category: 'device',
      severity: 'low',
      userMessage: 'Unable to toggle camera. Please try again.',
      technicalMessage: 'Failed to enable/disable camera',
      recoveryActions: [
        this.createRecoveryAction('retry', 'Try Again', 'Try toggling camera again', 1),
        this.createRecoveryAction('check_permission', 'Check Permission', 'Verify camera permissions', 2),
      ],
      shouldRetry: true,
      isTemporary: true
    });

    // Configuration errors
    this.addErrorClassification('ROOM_NOT_FOUND', {
      category: 'configuration',
      severity: 'medium',
      userMessage: 'Video room is no longer available.',
      technicalMessage: 'Video room does not exist or has been closed',
      recoveryActions: [
        this.createRecoveryAction('retry', 'Try Again', 'Check if room is available again', 1),
      ],
      shouldRetry: false,
      isTemporary: true
    });

    this.addErrorClassification('ROOM_FULL', {
      category: 'configuration',
      severity: 'medium',
      userMessage: 'Video room is full. Please try again later.',
      technicalMessage: 'Maximum participants limit reached',
      recoveryActions: [
        this.createRecoveryAction('wait_and_retry', 'Wait and Retry', 'Wait for someone to leave and try again', 1),
      ],
      shouldRetry: true,
      isTemporary: true
    });
  }

  private addErrorClassification(code: string, classification: ErrorClassification): void {
    this.errorClassifications.set(code, classification);
  }

  private createRecoveryAction(
    type: RecoveryActionType,
    label: string,
    description: string,
    priority: number
  ): RecoveryAction {
    return { type, label, description, priority };
  }

  private classifyByPattern(errorCode: string): ErrorClassification | null {
    if (!errorCode) {
      return null;
    }

    const code = errorCode.toLowerCase();

    // Network patterns
    if (code.includes('network') || code.includes('timeout') || code.includes('disconnect')) {
      return {
        category: 'network',
        severity: 'medium',
        userMessage: 'Network connection issue. Please check your internet and try again.',
        technicalMessage: `Network-related error: ${errorCode}`,
        recoveryActions: [
          this.createRecoveryAction('retry', 'Try Again', 'Retry the connection', 1),
          this.createRecoveryAction('check_network', 'Check Network', 'Verify internet connection', 2),
        ],
        shouldRetry: true,
        isTemporary: true
      };
    }

    // Permission patterns
    if (code.includes('permission') || code.includes('denied') || code.includes('access')) {
      return {
        category: 'permission',
        severity: 'high',
        userMessage: 'Permission denied. Please allow access to camera and microphone.',
        technicalMessage: `Permission-related error: ${errorCode}`,
        recoveryActions: [
          this.createRecoveryAction('check_permission', 'Grant Permission', 'Allow device access in browser', 1),
        ],
        shouldRetry: false,
        isTemporary: false
      };
    }

    // Server patterns
    if (code.includes('server') || code.includes('503') || code.includes('502')) {
      return {
        category: 'server',
        severity: 'high',
        userMessage: 'Video service is temporarily unavailable. Please try again later.',
        technicalMessage: `Server error: ${errorCode}`,
        recoveryActions: [
          this.createRecoveryAction('wait_and_retry', 'Wait and Retry', 'Try again in a moment', 1),
        ],
        shouldRetry: true,
        isTemporary: true
      };
    }

    return null;
  }

  private createFallbackClassification(): ErrorClassification {
    return {
      category: 'unknown',
      severity: 'medium',
      userMessage: 'An unexpected error occurred with the video service. Please try again.',
      technicalMessage: 'Unknown video error',
      recoveryActions: [
        this.createRecoveryAction('retry', 'Try Again', 'Attempt to reconnect', 1),
        this.createRecoveryAction('refresh_page', 'Refresh Page', 'Refresh and try again', 2),
        this.createRecoveryAction('contact_support', 'Contact Support', 'Report this issue', 3),
      ],
      shouldRetry: true,
      isTemporary: true
    };
  }

  private generateErrorId(error: VideoError): string {
    const timestamp = error.timestamp || Date.now();
    const code = error.code || 'UNKNOWN';
    return `${code}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Singleton instance for consistent error handling
export const videoErrorHandler = new VideoErrorHandler();